import { useState } from 'react';
import { ProductResult } from '../types';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

interface ProductResultsTableProps {
  products: ProductResult[];
  onProductSelect: (product: ProductResult) => void;
}

const ProductResultsTable = ({ products, onProductSelect }: ProductResultsTableProps) => {
  const [sortField, setSortField] = useState<keyof ProductResult>('code');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: keyof ProductResult) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedProducts = [...products].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    return 0;
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('code')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  Código
                  <Icon 
                    name={sortField === 'code' && sortDirection === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                    size={16} 
                    color="currentColor" 
                  />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('description')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  Descrição
                  <Icon 
                    name={sortField === 'description' && sortDirection === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                    size={16} 
                    color="currentColor" 
                  />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">NCM</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">CEST</th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('totalValue')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors ml-auto"
                >
                  Valor Total
                  <Icon 
                    name={sortField === 'totalValue' && sortDirection === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                    size={16} 
                    color="currentColor" 
                  />
                </button>
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground">
                Tributos
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground">
                Alíquota Efetiva
              </th>
              <th className="px-4 py-3 text-center text-sm font-medium text-foreground">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedProducts.map((product) => (
              <tr key={product.id} className="hover:bg-muted/50 transition-colors">
                <td className="px-4 py-3">
                  <span className="text-sm font-mono text-foreground">{product.code}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground">{product.description}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-mono text-foreground">{product.ncm}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-mono text-foreground">{product.cest}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-medium text-foreground">
                    {formatCurrency(product.totalValue)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-medium text-primary">
                    {formatCurrency(product.taxCalculation.totalTax)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-mono text-foreground">
                    {product.taxCalculation.effectiveRate.toFixed(2)}%
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    onClick={() => onProductSelect(product)}
                  >
                    Detalhes
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-3 bg-muted border-t border-border">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Total de {products.length} produtos</span>
          <div className="flex items-center gap-4">
            <span>
              Valor Total: {formatCurrency(products.reduce((sum, p) => sum + p.totalValue, 0))}
            </span>
            <span className="text-primary font-medium">
              Tributos: {formatCurrency(products.reduce((sum, p) => sum + p.taxCalculation.totalTax, 0))}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductResultsTable;